export const navLinks = [
  {
    label: 'Services',
    children: [
      { label: 'AI Receptionist', to: '/ai-receptionist' },
      { label: 'Website Chatbot', to: '/website-chatbot' },
      { label: 'Websites', to: '/websites' },
      { label: 'Complete Setup', to: '/complete' },
    ],
  },
  {
    label: 'Industries',
    children: [
      { label: 'Trades', to: '/industries/trades' },
      { label: 'Plumbers', to: '/industries/plumbers' },
      { label: 'Electricians', to: '/industries/electricians' },
      { label: 'HVAC', to: '/industries/hvac' },
    ],
  },
  { label: 'Custom Software', to: '/custom-software' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Work', to: '/work' },
  { label: 'About', to: '/about' },
];

export const serviceLinks = [
  { label: 'AI Receptionist', to: '/ai-receptionist', description: 'Answer calls, capture details and book jobs.' },
  { label: 'Website Chatbot', to: '/website-chatbot', description: 'Collect enquiries from your website 24/7.' },
  { label: 'Websites', to: '/websites', description: 'Websites built to get enquiries for trades and service businesses.' },
  { label: 'Complete Setup', to: '/complete', description: 'Website, chatbot and AI receptionist, managed.' },
];

export const industryLinks = [
  { label: 'Trades', to: '/industries/trades' },
  { label: 'Plumbers', to: '/industries/plumbers' },
  { label: 'Electricians', to: '/industries/electricians' },
  { label: 'HVAC', to: '/industries/hvac' },
  { label: 'Mowing and Lawn Care', to: '/industries/mowing' },
  { label: 'Cleaning', to: '/industries/cleaning' },
];

export const customSoftwareLinks = [
  { label: 'Custom Software', to: '/custom-software' },
  { label: 'MVP Development', to: '/mvp-development' },
  { label: 'SaaS Development', to: '/saas-development' },
  { label: 'Mobile Apps', to: '/mobile-app-development' },
  { label: 'AI Integration', to: '/ai-integration' },
];

export const companyLinks = [
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
  { label: 'Press', to: '/press' },
  { label: 'Hervey Bay', to: '/hervey-bay' },
  { label: 'Case Studies', to: '/case-studies' },
  { label: 'BulletChat', to: '/bullet-chat' },
  { label: 'Guides', to: '/guides' },
  { label: 'Integrations', to: '/integrations' },
  { label: 'Missed Call Calculator', to: '/tools/missed-call-calculator' },
];

export const legalLinks = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms of Use', to: '/terms-of-use' },
  { label: 'AI and Data Policy', to: '/ai-data-policy' },
];

export const offers = [
  {
    key: 'websiteChatbot',
    name: 'Website + Chatbot',
    description: 'A website built to get enquiries, with a chatbot we set up and run. No voice receptionist.',
    includes: [
      'Mobile-first website and service pages',
      'Enquiry forms and tracking of enquiries',
      '8,000 chatbot messages per month',
      'Chatbot training and installation',
      'Hosting, care and support',
    ],
    bestFor: 'businesses that want a site that captures leads, without phone answering.',
  },
  {
    key: 'websiteChatbotVoice',
    name: 'Website + Chatbot + AI Receptionist',
    description: 'The full lead-capture setup: website, chatbot and a voice AI receptionist that answers and books calls.',
    includes: [
      'Everything in Website + Chatbot',
      '300 voice minutes per month',
      'AI phone answering, after-hours and when you are busy',
      'Booking, rescheduling and call summaries',
      'We set it up and run it for you',
    ],
    bestFor: 'trades and service businesses that miss calls while on the tools.',
  },
];

export const industries = [
  {
    slug: 'plumbers',
    name: 'Plumbers',
    metaLabel: 'plumbing',
    intro: 'Plumbing work is urgent. A missed call is usually a job that goes to the next plumber.',
    painPoints: [
      'You are under a sink or driving when the phone rings.',
      'After-hours leaks and blocked drains go to voicemail.',
      'Quote requests arrive without the address, access details or photos you need.',
    ],
    scenarios: [
      'Emergency leak or burst pipe',
      'Blocked drain or toilet',
      'Hot water system failure',
      'Quote request for a bathroom or renovation',
      'Reschedule or confirm an existing job',
    ],
    captures: ['Name and mobile', 'Suburb and job address', 'Job type and urgency', 'Photos or access notes where supported', 'Preferred booking time'],
    faqs: [
      { question: 'Can the AI handle emergency plumbing calls?', answer: 'It can identify an urgent call, capture the details and pass it to a person according to your rules. For a gas leak, fire or dangerous situation, it should tell the caller to call 000 and notify you.' },
      { question: 'Can it book a plumber into my calendar?', answer: 'Yes, where the calendar and job system are connected. CloudNext confirms the booking rules and tests the flow before go live.' },
      { question: 'What if the caller wants a price?', answer: 'The AI can share the pricing rules you approve or take the details for a quote. It should not invent a price.' },
    ],
  },
  {
    slug: 'electricians',
    name: 'Electricians',
    metaLabel: 'electrical',
    intro: 'Electrical calls are urgent and high-value. The caller often rings two or three electricians before one answers.',
    painPoints: [
      'You cannot answer while isolating a circuit or on a roof.',
      'After-hours faults and safety calls go unanswered.',
      'The first call often lacks the switchboard, address or access details.',
    ],
    scenarios: [
      'Power outage or tripping circuit',
      'Safety switch or switchboard issue',
      'Lighting, fan or power point installation',
      'Quote request for a renovation or solar job',
      'Reschedule or confirm an existing job',
    ],
    captures: ['Name and mobile', 'Suburb and site address', 'Fault type and urgency', 'Access and safety notes', 'Preferred booking time'],
    faqs: [
      { question: 'Can the AI handle electrical safety calls?', answer: 'It can capture the details and pass urgent calls to a person. For a live electrical hazard, fire or medical emergency, it should tell the caller to call 000 and not give technical advice.' },
      { question: 'Can it book an electrician after hours?', answer: 'Yes, if the calendar and after-hours rules are configured. You decide which jobs are offered after hours and which are booked for the next business day.' },
      { question: 'Can it answer questions about solar or EV chargers?', answer: 'It can answer from the information you approve and capture the details. It should route technical or quote questions to you.' },
    ],
  },
  {
    slug: 'hvac',
    name: 'HVAC',
    metaLabel: 'HVAC',
    intro: 'HVAC work is seasonal and urgent. When the air conditioning fails, the caller wants a booking, not a voicemail.',
    painPoints: [
      'Peak-season calls arrive faster than you can answer.',
      'After-hours breakdowns go to voicemail.',
      'Service calls and install quotes need different questions.',
    ],
    scenarios: [
      'Air conditioner not cooling or heating',
      'Routine service and filter cleaning',
      'New install or replacement quote',
      'Warranty or maintenance follow-up',
      'Reschedule or confirm an existing job',
    ],
    captures: ['Name and mobile', 'Suburb and site address', 'System type and issue', 'Preferred service window', 'Access and parking notes'],
    faqs: [
      { question: 'Can the AI tell a service call from an install quote?', answer: 'Yes, with the call handling you approve. It can ask different questions for a breakdown, a service and a new install.' },
      { question: 'Can it handle peak-season calls?', answer: 'Yes. It can answer calls when your team is busy and book the ones that fit your rules.' },
      { question: 'Can it book a specific time window?', answer: 'It can offer the windows you approve, subject to the calendar and job-system connection.' },
    ],
  },
  {
    slug: 'mowing',
    name: 'Mowing and Lawn Care',
    metaLabel: 'mowing and lawn care',
    intro: 'Recurring lawn work lives on scheduling. Missed calls and slow quotes cost repeat customers.',
    painPoints: [
      'You are on a mower or trailer for most of the day.',
      'Quote requests need the suburb, property size and frequency.',
      'Reschedules and weather changes create phone tag.',
    ],
    scenarios: [
      'New recurring mowing quote',
      'One-off clean-up or overgrown lawn',
      'Reschedule due to weather',
      'Additional services such as hedging or edging',
      'Confirm an upcoming visit',
    ],
    captures: ['Name and mobile', 'Suburb and property address', 'Property size and frequency', 'Preferred day or window', 'Photos where supported'],
    faqs: [
      { question: 'Can the AI quote a mowing job?', answer: 'It can share the pricing rules you approve or take the details for a quote. It should not guess a price for a property it cannot see.' },
      { question: 'Can it handle weather reschedules?', answer: 'Yes, if you define the rules. It can move bookings to the next available window and notify you.' },
      { question: 'Can it capture recurring job details?', answer: 'Yes. It can capture frequency, property size, access notes and preferred days.' },
    ],
  },
  {
    slug: 'cleaning',
    name: 'Cleaning',
    metaLabel: 'cleaning',
    intro: 'Cleaning enquiries are often price-sensitive and time-sensitive. Fast, clear answers win the booking.',
    painPoints: [
      'You are cleaning, not sitting by the phone.',
      'Quotes need the property size, frequency and extras.',
      'Last-minute bookings and reschedules are hard to manage.',
    ],
    scenarios: [
      'New regular clean quote',
      'One-off deep clean or end-of-lease clean',
      'Reschedule or cancel',
      'Add-on services such as windows or carpets',
      'Confirm an upcoming clean',
    ],
    captures: ['Name and mobile', 'Suburb and property address', 'Property size and frequency', 'Extras and access notes', 'Preferred booking time'],
    faqs: [
      { question: 'Can the AI quote a cleaning job?', answer: 'It can share the pricing rules you approve or take the details for a quote. It should not promise a price for a property it has not assessed.' },
      { question: 'Can it handle last-minute bookings?', answer: 'Yes, if the calendar and rules allow it. You decide the minimum notice and which slots are available.' },
      { question: 'Can it reschedule or cancel?', answer: 'Yes. It can apply your notice and cancellation rules and send a confirmation.' },
    ],
  },
];

export const guides = [
  {
    slug: 'what-is-an-ai-receptionist',
    title: 'What is an AI receptionist and how does it work?',
    description: 'A plain-English guide to AI receptionists: what they do, how they answer calls, what they can book and what to check before buying.',
    answer: 'An AI receptionist is a voice agent that answers phone calls, answers common questions, captures the caller\'s details and books appointments. It works after hours and during busy periods, then sends a summary to the business. It is different from a website chatbot, which handles text chat on a website.',
    sections: [
      {
        heading: 'What an AI receptionist actually does',
        paragraphs: [
          'It answers the phone with your business name, works out what the caller needs, and follows call handling you approve. That can include booking, rescheduling, taking a message, answering common questions or transferring to a person.',
          'It is not a press-1 phone menu. The caller speaks normally. The AI turns speech into text, matches the request to your services and rules, and responds in a natural voice.',
        ],
      },
      {
        heading: 'Where it helps most',
        paragraphs: [
          'It helps when calls arrive while you are on the tools, driving, with a customer or closed for the day. For trades, the common wins are after-hours calls, calls during busy periods and quote requests that would otherwise go to voicemail.',
          'It also helps when the same questions come up all day: availability, service areas, pricing rules, parking, access and what to do before a visit.',
        ],
      },
      {
        heading: 'What it is not',
        paragraphs: [
          'It is not a replacement for every human conversation. Complex complaints, safety issues and high-value negotiations should go to a person. A good setup has clear rules for passing calls to a person.',
          'It is also not a guarantee that every call becomes a booking. It is a tool for answering, qualifying and capturing. The quality depends on how calls are handled, the knowledge base and the booking rules.',
        ],
      },
      {
        heading: 'What to check before buying',
        paragraphs: [
          'Ask how the AI handles a caller who asks for a human, a price outside your rules, a wrong number, a poor line, an abusive caller or an emergency. Ask how bookings go into your calendar or job system. Ask where calls and transcripts are stored, how long they are kept and who can access them.',
          'Ask to try the AI yourself, not just watch a recorded sales video. Call it at night if possible, and try to break it.',
        ],
      },
    ],
    faqs: [
      { question: 'Is an AI receptionist the same as a chatbot?', answer: 'No. A chatbot handles text on a website. An AI receptionist handles phone calls.' },
      { question: 'Will callers know they are talking to an AI?', answer: 'They should. A good setup discloses that the caller is speaking to an AI at the start of the call.' },
      { question: 'Can it work after hours?', answer: 'Yes. After-hours answering is one of the main reasons businesses use it.' },
    ],
  },
  {
    slug: 'ai-receptionist-cost',
    title: 'How much does an AI receptionist cost in Australia?',
    description: 'AI receptionist pricing in Australia: setup fees, monthly plans, included minutes, extra minute charges and what changes the cost.',
    answer: 'Australian AI receptionist services we reviewed advertised plans from about $200 to $600 per month as of November 2026, depending on call volume, after-hours cextra minute charges and connections. Setup fees vary. CloudNext confirms its own setup fee, monthly fee and included minutes on a 15-minute call. Final price depends on how calls are handled and which systems it connects to.',
    sections: [
      {
        heading: 'The four parts of the price',
        paragraphs: [
          'Most quotes have four parts: a setup fee, a monthly fee, a number of included minutes and the cost of connections to your other software. Some providers also charge for the phone number, extra numbers or premium support.',
          'Ask what happens when you go over the included minutes. A low monthly price with a small allowance can be more expensive than a higher plan with enough minutes for your call volume.',
        ],
      },
      {
        heading: 'What changes the cost',
        paragraphs: [
          'Call volume, after-hours coverage, the number of services and locations, the complexity of how calls are handled, booking rules, calendar and job-system connections, SMS confirmations, multiple staff or technicians, and reporting all affect the price.',
          'A simple after-hours message-taking service costs less than a full booking and rescheduling agent connected to your job system.',
        ],
      },
      {
        heading: 'Questions to ask about price',
        paragraphs: [
          'What is included in the monthly fee? How many minutes? What is the extra minute rate? Is the phone number included? Are SMS confirmations included? Is setup one-off or does it include training and changes? What is the notice period? What happens if the AI cannot answer?',
          'Ask for the total first-year cost, not just the monthly fee.',
        ],
      },
      {
        heading: 'How CloudNext prices it',
        paragraphs: [
          'CloudNext uses a setup fee plus a monthly service fee, with a defined number of included minutes. The final quote depends on how calls are handled, the number of services, the booking rules and the connections to your other software. Pricing is confirmed on the call before anything is signed.',
          'The setup fee is waived for the first five trades businesses. The offer ends 31 December 2026. The terms and pricing are confirmed on the call before anything is signed.',
        ],
      },
    ],
    faqs: [
      { question: 'Is there a cheap AI receptionist?', answer: 'There are low-cost tools, but check the included minutes, how calls are handled, the booking connection and the support. The cheapest option can cost more if it loses bookings or cannot transfer to a person.' },
      { question: 'Do I pay per call or per minute?', answer: 'It varies. Many providers include a monthly allowance and charge for extra minutes. Ask for the extra minute rate in writing.' },
      { question: 'Is setup included?', answer: 'Usually not. Setup covers how calls are handled, knowledge base, number, connections and testing. Ask what the setup includes and what counts as a change later.' },
    ],
  },
  {
    slug: 'ai-receptionist-vs-answering-service',
    title: 'AI receptionist vs answering service: which is right for your business?',
    description: 'A practical comparison of AI receptionists, human answering services, voicemail and hiring a receptionist for Australian small businesses.',
    answer: 'An answering service uses human operators to answer calls and take messages. An AI receptionist uses a voice agent to answer, qualify and book appointments. A human service can handle nuanced conversations; an AI can answer instantly, after hours, at a lower marginal cost. Some businesses use both: AI for busy times and after hours, humans for complex calls.',
    sections: [
      {
        heading: 'The four options',
        paragraphs: [
          'Voicemail is cheap but loses callers. A human answering service answers and takes a message, but may not know your calendar or job system. An AI receptionist can answer, qualify and book, but needs clear rules. A full-time receptionist is the most flexible but the highest fixed cost.',
          'The right choice depends on call volume, how technical the calls are and how much of the conversation can be handled by a script.',
        ],
      },
      {
        heading: 'Where AI is stronger',
        paragraphs: [
          'AI is strong for after-hours calls, calls when you are busy, repetitive questions, booking, rescheduling and capturing structured details. It answers instantly and does not get tired. It can work across multiple numbers and locations.',
          'It is also consistent. Every caller gets the same questions and the same rules.',
        ],
      },
      {
        heading: 'Where humans are stronger',
        paragraphs: [
          'Humans are stronger for complaints, complex negotiations, upset callers and situations that need judgement. A good AI setup transfers those calls to a person.',
          'For a high-value quote or a sensitive job, a human conversation may still be the better experience.',
        ],
      },
      {
        heading: 'The hybrid option',
        paragraphs: [
          'Many small businesses do not need to choose one. AI can answer after hours and when you are busy, then send a summary to the team. During business hours, the team can answer and let the AI handle the calls they miss.',
          'The key is to define the rules for passing calls to a person and test them.',
        ],
      },
    ],
    faqs: [
      { question: 'Is an AI receptionist cheaper than an answering service?', answer: 'It can be, especially after hours or for high call volumes. Compare the total cost, including setup, minutes, extra minute charges and connections, not just the monthly fee.' },
      { question: 'Can an AI receptionist transfer to a human?', answer: 'Yes, if the phone system and call handling support it. Ask how the transfer works and what happens if the human does not answer.' },
      { question: 'Can I use both?', answer: 'Yes. A common setup is AI for after hours and busy times, with humans handling business-hours calls.' },
    ],
  },
  {
    slug: 'can-an-ai-receptionist-book-appointments',
    title: 'Can an AI receptionist book appointments in my calendar?',
    description: 'How AI receptionists book, reschedule and cancel appointments, what connections are needed and what can go wrong.',
    answer: 'Yes, if the AI receptionist is connected to a calendar or job system and the booking rules are configured. It can offer available slots, capture the caller\'s details, write the booking into the calendar, send a confirmation and handle reschedules or cancellations. The quality depends on the connection and the rules you set.',
    sections: [
      {
        heading: 'How the booking works',
        paragraphs: [
          'The AI reads your availability, applies the service duration, buffers, staff availability and existing appointments, then offers a slot that actually exists. It captures the caller\'s name, number, address and job details, reads them back and confirms.',
          'The booking is then written to the calendar or job system, and a confirmation is sent by SMS or email.',
        ],
      },
      {
        heading: 'What you need to set up',
        paragraphs: [
          'You need a calendar or job system with a connection, your service types and durations, your booking rules, your service area, your staff or technician availability, and your notice and cancellation rules.',
          'You also need a plan for when the calendar is unavailable or the booking fails.',
        ],
      },
      {
        heading: 'What can go wrong',
        paragraphs: [
          'Double bookings, time-zone mistakes, wrong service durations, unavailable staff, stale availability and callers who change their mind. A good setup tests these cases and has a plan for when it can’t answer.',
          'The AI should confirm the details before finalising and send a confirmation the caller can check.',
        ],
      },
      {
        heading: 'What to ask a provider',
        paragraphs: [
          'Ask which calendars and job systems are supported, whether the connection is live or planned, how availability is read, how double bookings are prevented, how reschedules and cancellations work, and what happens if the calendar is down.',
          'Ask for a test booking in your own calendar before you sign.',
        ],
      },
    ],
    faqs: [
      { question: 'Can it book into Google Calendar or Outlook?', answer: 'Yes, where the connection is supported. CloudNext confirms the calendar and job-system connections on the call.' },
      { question: 'Can it reschedule or cancel?', answer: 'Yes, if the rules and the connection allow it. Ask how the AI verifies the caller before changing a booking.' },
      { question: 'Can it book multiple staff or technicians?', answer: 'It can, if the connection and rules support staff selection. This is more complex than a single calendar and should be tested.' },
    ],
  },
  {
    slug: 'what-happens-if-ai-cannot-answer',
    title: 'What happens when an AI receptionist cannot answer a call?',
    description: 'What happens when an AI receptionist can’t answer: passing calls to a person, taking messages and handling emergencies.',
    answer: 'A well-configured AI receptionist has a plan for every call it cannot handle. It can transfer to a person, take a structured message, offer a callback, answer from approved information or pass an urgent call to a person. It should never invent an answer or give unsafe technical advice. What happens when it can’t answer is part of the setup and should be tested.',
    sections: [
      {
        heading: 'What happens if it can’t answer',
        paragraphs: [
          'The AI can transfer the call to a nominated person or team, take a message with the details you need, offer a callback, send the caller a booking link, or end the call politely and notify the business.',
          'For urgent or safety-related calls, the script should tell the caller to contact the right emergency service and notify the business immediately.',
        ],
      },
      {
        heading: 'When to transfer to a human',
        paragraphs: [
          'Transfer when the caller asks for a person, the request is outside the approved knowledge, the caller is upset, the job is high-value or complex, or the AI is not confident. It is better to transfer than to guess.',
          'Define who receives the transfer, what happens if they do not answer, and how the AI hands over the context.',
        ],
      },
      {
        heading: 'When to take a message',
        paragraphs: [
          'Take a message when no one is available, the caller does not want to book yet, or the request needs a quote or site visit. Capture the name, number, suburb, job type, urgency and preferred callback time.',
          'Send the message to the team by email, SMS or CRM, with a summary of the call.',
        ],
      },
      {
        heading: 'Test what happens if it can’t answer',
        paragraphs: [
          'Call the AI and ask for a human. Ask a question the AI should not answer. Call after hours. Pretend the line is bad. See what happens.',
          'A setup that only works for a perfect call is not ready.',
        ],
      },
    ],
    faqs: [
      { question: 'Can the AI transfer to my mobile?', answer: 'Yes, if the phone system supports it. Ask how the transfer works and what happens if you do not answer.' },
      { question: 'Does it take a message?', answer: 'Yes. A good setup captures the details you need and sends them to your team.' },
      { question: 'What about emergencies?', answer: 'The AI should tell the caller to contact 000 for an emergency, notify the business and avoid giving technical or safety advice.' },
    ],
  },
  {
    slug: 'do-i-need-to-change-my-phone-number',
    title: 'Do I need to change my phone number to use an AI receptionist?',
    description: 'How call forwarding, new numbers and number porting work with an AI receptionist, and what to test before switching.',
    answer: 'In most cases, no. You can keep your existing number and forward calls to the AI receptionist, or use a new number for after-hours and busy times. Porting is possible but slower and riskier. The right setup depends on your phone system, your carrier and how you want calls to flow. Test the forwarding and what happens when it can’t answer before you switch.',
    sections: [
      {
        heading: 'Option 1: forward your existing number',
        paragraphs: [
          'This is the simplest option. You keep the number your customers already know and forward calls to the AI when you are busy, after hours or all the time. You can usually choose the conditions.',
          'Check what happens when the AI transfers back to you or when the forwarding fails.',
        ],
      },
      {
        heading: 'Option 2: use a new number',
        paragraphs: [
          'A new number is useful for after-hours or busy times. You can advertise it separately and keep your main number for existing customers.',
          'It also makes it easier to measure how many calls the AI handles.',
        ],
      },
      {
        heading: 'Option 3: port your number',
        paragraphs: [
          'Porting moves your number to a new provider. It can take days or weeks and can disrupt calls if it is not planned. It is usually not necessary for an AI receptionist.',
          'If you port, test the number, the forwarding and what happens when it can’t answer before cancelling the old service.',
        ],
      },
      {
        heading: 'What to test',
        paragraphs: [
          'Call from a mobile and a landline. Test after hours. Test when the AI is unavailable. Test a transfer. Test a booking. Make sure the caller experience is what you expect.',
          'Do not switch your main number until the new setup has passed a full week of tests.',
        ],
      },
    ],
    faqs: [
      { question: 'Can I keep my current number?', answer: 'Usually yes. Forwarding is the common approach. Check with your carrier and test it before go live.' },
      { question: 'Will callers know the number changed?', answer: 'If you forward, they keep dialling the same number. If you use a new number, tell your customers and update your website and listings.' },
      { question: 'How long does porting take?', answer: 'It varies by carrier. Plan for days to weeks and keep the old service active until the new one works.' },
    ],
  },
];

export const homeFaqs = [
  { question: 'What does CloudNext do?', answer: 'CloudNext is an Australia-based software studio. It sets up websites, website chatbots and voice AI receptionists for trades and service businesses, and builds custom software, MVPs and SaaS platforms.' },
  { question: 'Do you only work with trades?', answer: 'Trades are the first focus because missed calls and bookings are a clear problem. The same setup can work for other service businesses. CloudNext also builds custom software for other industries.' },
  { question: 'Do I need to change my phone number?', answer: 'Usually no. In most cases your existing number can forward to the AI receptionist. CloudNext tests the forwarding and what happens when it can’t answer before go live.' },
  { question: 'Can I try the AI before I buy?', answer: 'Yes. We will set up a demo for you before you commit. Contact us and we will walk you through how it handles calls for your business, using your services, hours and booking rules.' },
  { question: 'Is this the same as BulletChat?', answer: 'BulletChat is the self-service chatbot platform. CloudNext is the service that sets up and manages the chatbot, AI receptionist and booking for you.' },
  { question: 'How much does it cost?', answer: 'Pricing depends on how calls are handled, minutes, connections and support. The pricing page explains the components, and the final quote is confirmed on a 15-minute call.' },
];

export const aiReceptionistFaqs = [
  { question: 'What is an AI receptionist?', answer: 'It is a voice agent that answers phone calls, answers common questions, captures the caller\'s details and books appointments. It can work after hours and during busy periods.' },
  { question: 'Can it book jobs into my calendar?', answer: 'Yes, where the calendar or job system is connected and the booking rules are configured. CloudNext tests the booking, reschedule and cancellation flows before go live.' },
  { question: 'What happens if it cannot answer?', answer: 'It can transfer to a person, take a structured message, offer a callback or pass an urgent call to a person. What happens when it can’t answer is part of the setup.' },
  { question: 'Do I need to change my number?', answer: 'Usually no. You can forward your existing number or use a separate number for after hours and when you are busy.' },
  { question: 'Does it sound robotic?', answer: 'It uses a natural voice, but it is still an AI. A good setup discloses that the caller is speaking to an AI.' },
  { question: 'Can it handle Australian accents and names?', answer: 'That is part of the testing. We check local place names and street names during setup. Test before you commit.' },
  { question: 'Is it safe for emergencies?', answer: 'It should never give technical or safety advice. For gas, fire, electrical hazards or medical emergencies, it should tell the caller to call 000 and notify the business.' },
  { question: 'Who owns the call data?', answer: 'That should be set out in the agreement. CloudNext\'s default position is that the client owns its content and call data, and CloudNext has a licence to use it to provide the service.' },
];

export const chatbotFaqs = [
  { question: 'What does the website chatbot do?', answer: 'It answers common questions, captures the visitor\'s name and contact details, and can start a booking request. It works 24/7 on your website.' },
  { question: 'Is it the same as BulletChat?', answer: 'It is powered by BulletChat. CloudNext sets it up, trains it on your business and manages it for you.' },
  { question: 'Can it book appointments?', answer: 'It can capture a booking request and, where the calendar is connected, offer available times. The exact steps depend on the connection.' },
  { question: 'How is it trained?', answer: 'On your services, hours, FAQs, pricing rules and booking rules. You approve the answers before go live.' },
  { question: 'What happens if it cannot answer?', answer: 'It can take a message, offer a callback or hand over to your team. It should not invent an answer.' },
];

export const websitesFaqs = [
  { question: 'What kind of websites do you build?', answer: 'Websites for trades and service businesses: clear services, service areas, proof, lead forms and fast mobile pages. They can include a chatbot and booking.' },
  { question: 'Do you use templates?', answer: 'The build starts from a proven structure, then the design and content are shaped around the business. You are not left with a generic template.' },
  { question: 'How long does a website take?', answer: 'It depends on the number of pages, content and connections. A focused trade website is usually weeks, not months. The timeline is confirmed before the build starts.' },
  { question: 'Can you improve my existing site?', answer: 'Yes, if the foundation is sound. If it is not, a rebuild is often faster and cheaper than patching it.' },
  { question: 'Does it include SEO?', answer: 'The build includes the technical basics, clear page structure and content. Ongoing SEO and content are separate.' },
];

export const pricingFaqs = [
  { question: 'Is there anything extra to pay?', answer: 'For most businesses, there is nothing extra to pay CloudNext. The bundle covers the standard setup, the included minutes and ongoing support. Only businesses with special requirements, such as custom connections or extra locations, need customisations, and we quote those upfront before any work starts.' },
  { question: 'What happens from 1 January 2027?', answer: 'The special setup price ends on 31 December 2026. From 1 January 2027, the standard setup and monthly prices apply. Clients who have signed before that date keep the terms in their agreement.' },
  { question: 'Do you sell websites on their own?', answer: 'No. Websites are part of the Website + Chatbot bundle or the Website + Chatbot + AI Receptionist bundle. If you only need a website, contact us and we will tell you whether we can help.' },
  { question: 'Why is the setup 50% off?', answer: 'It is a special offer that ends on 31 December 2026. The standard setup applies from 1 January 2027.' },
  { question: 'Can I cancel?', answer: 'Yes, any time. Just tell us before your next billing date and you will not be charged again. You keep the service until the end of the period you have already paid for.' },
  { question: 'What happens if I go over the included minutes?', answer: 'Extra minutes cost $0.30/min on the 300-minute bundle and $0.18/min on the 1,000-minute bundle.' },
  { question: 'Do I need to change my phone number?', answer: 'Usually no. Forwarding is the common approach. The setup is tested before go live.' },
  { question: 'Who owns my call recordings?', answer: 'You do. You own your content and your call data, and CloudNext has a licence to use it only to run your service. The agreement states this in writing.' },
  { question: 'What is the setup waiver?', answer: 'The setup fee is waived for the first five trades businesses who sign up before 31 December 2026. Monthly pricing stays as shown above.' },
];

export const customSoftwareFaqs = [
  { question: 'What kind of software do you build?', answer: 'Custom web apps, mobile apps, MVPs, SaaS platforms, internal tools, portals and AI integrations.' },
  { question: 'Do you work with existing codebases?', answer: 'Yes, if the codebase is in a stack we can support. We review it first and tell you honestly whether to improve or rebuild.' },
  { question: 'How is pricing structured?', answer: 'Fixed fee for defined work, with milestone or monthly options for larger projects. The scope and price are agreed before work starts.' },
  { question: 'Who owns the code?', answer: 'That is set out in the contract. You can own the code on completion, or we can agree another arrangement.' },
  { question: 'Do you sign NDAs?', answer: 'Yes. We can sign an NDA before discussing confidential details.' },
  { question: 'Can you help with an MVP?', answer: 'Yes. We scope the smallest version that proves the idea, build it, and help you decide what to do next.' },
];

export const softwareTestimonials = [
  {
    quote: 'Orfeo was fantastic in helping me build and deploy a web app for my business. He took my project brief and worked with me to build it into an MVP which is deployed and ready to take to market.',
    author: 'Riley',
    role: 'Upwork client, custom software',
  },
  {
    quote: 'From start to finish, Orfeo has been fantastic to deal with. Professional, responsive, and incredibly skilled. The final result exceeded my expectations.',
    author: 'Benn Ward',
    role: 'Upwork client, custom software',
  },
  {
    quote: 'A pleasure to work with. Excellent communication and follow-up. The fixed price contract gave great confidence. Flawless completion of the app within the time constraints.',
    author: 'Chris Kolle',
    role: 'Upwork client, custom software',
  },
];

export const portfolio = [
  { title: 'Big Voice Prodigy', description: 'Secure data destruction and certified e-waste recycling.', url: 'https://bigvoiceprodigy.my', image: '/portfolio/bigvoiceprodigy.jpg', type: 'Client website', tags: ['Website', 'Sustainability'] },
  { title: 'Sandhu Accounting & Taxation', description: 'A professional accounting and taxation website built to build trust and drive enquiries.', url: 'https://sandhuats.com.au', image: '/portfolio/sandhu-accounting.jpg', type: 'Client website', tags: ['Website', 'Professional services'] },
  { title: 'Siren Ella', description: 'Mermaid and freediving classes presented with an immersive, ocean-inspired website.', url: 'https://sirenella.peakk.net', image: '/portfolio/siren-ella.jpg', type: 'Client website', tags: ['Website', 'Experience'] },
  { title: 'Integral', description: 'A publication hub for stories, archives and announcements.', url: 'https://integral.peakk.net', image: '/portfolio/integral.jpg', type: 'Client website', tags: ['Website', 'Publishing'] },
  { title: 'Prolifyc', description: 'A streetwear e-commerce store with a bold collection launch and a clean shop flow.', url: 'https://prolifyc.peakk.net', image: '/portfolio/prolifyc.jpg', type: 'Client website', tags: ['E-commerce', 'Apparel'] },
  { title: 'Tops Asia', description: 'A restaurant website for Asian rice bowls with menu highlights and table booking.', url: 'https://topsasia.cloudnext.dev', image: '/portfolio/tops-asia.jpg', type: 'Client website', tags: ['Website', 'Hospitality'] },
  { title: 'PowerPro Electrical', description: 'A demo website built to get enquiries for a modern electrical services business.', url: 'https://demo-electrical.cloudnext.dev', image: '/portfolio/powerpro-electrical.jpg', type: 'Demo website', tags: ['Demo', 'Trades'] },
  { title: 'Pristine', description: 'A premium home and office cleaning website with services, pricing and quote requests.', url: 'https://demo-clean.cloudnext.dev', image: '/portfolio/pristine-cleaning.jpg', type: 'Demo website', tags: ['Demo', 'Cleaning'] },
  { title: 'GreenCuts', description: 'A lawn mowing and yard care website built to turn visitors into quote requests.', url: 'https://demo-mowing.cloudnext.dev', image: '/portfolio/greencuts-mowing.jpg', type: 'Demo website', tags: ['Demo', 'Trades'] },
  { title: 'Apex Plumbing', description: 'A master plumbers website built to capture quote requests and showcase services.', url: 'https://demo-plumbing.cloudnext.dev', image: '/portfolio/apex-plumbing.jpg', type: 'Demo website', tags: ['Demo', 'Trades'] },
  { title: 'BulletChat', description: 'A self-service chatbot platform for small businesses.', url: 'https://bulletchat.app', image: '/assets/images/og-image.png', type: 'Product', tags: ['SaaS', 'AI'] },
  { title: 'Loope', description: 'Job management for trade businesses, with scheduling, invoicing and Xero.', url: 'https://loope.app', image: '/assets/images/og-image.png', type: 'Product', tags: ['SaaS', 'Trades'] },
];

export const integrations = [
  { group: 'Calendars', items: ['Google Calendar', 'Outlook Calendar'], status: 'Usually included' },
  { group: 'Job management', items: ['ServiceM8', 'Tradify', 'Fergus', 'simPRO', 'Jobber'], status: 'Quoted before you sign' },
  { group: 'CRM and accounting', items: ['Xero', 'HubSpot', 'Pipedrive', 'Zoho'], status: 'Quoted before you sign' },
  { group: 'Payments', items: ['Stripe'], status: 'Quoted before you sign' },
  { group: 'Custom systems', items: ['Webhooks', 'Custom API'], status: 'Quoted before you sign' },
];

export const customSoftwareServices = [
  {
    slug: 'mvp-development',
    name: 'MVP Development',
    title: 'MVP development for Australian founders and businesses',
    description: 'Build the smallest version of your idea that proves whether people will use it, without building extras you don’t need.',
    audience: 'Founders and business owners validating a new product or workflow.',
    bullets: ['Discovery and scope', 'Clickable prototype or build-ready spec', 'A focused first release', 'Analytics and feedback loops', 'A clear next-step plan'],
    faqs: [
      { question: 'How long does an MVP take?', answer: 'It depends on scope. A focused MVP can take weeks, not months. The scope and timeline are agreed before work starts.' },
      { question: 'What if I do not have a technical background?', answer: 'That is normal. We translate the idea into a build plan and explain the trade-offs in plain English.' },
      { question: 'Can you help after launch?', answer: 'Yes. We can support, maintain and extend the MVP once it is live.' },
    ],
  },
  {
    slug: 'saas-development',
    name: 'SaaS Development',
    title: 'SaaS development for Australian businesses',
    description: 'Design and build a SaaS product with subscriptions, accounts, billing and the workflows your customers need.',
    audience: 'Founders and businesses turning a service or internal tool into a SaaS product.',
    bullets: ['Product and architecture', 'Accounts and permissions', 'Subscription billing', 'Admin and reporting', 'Ongoing delivery'],
    faqs: [
      { question: 'Can you build a multi-tenant SaaS?', answer: 'Yes, where the product requires it. The architecture is agreed during discovery.' },
      { question: 'Can you integrate Stripe?', answer: 'Yes, where payments and subscriptions are part of the scope.' },
      { question: 'Do you provide ongoing support?', answer: 'Yes. Support and maintenance are scoped separately after launch.' },
    ],
  },
  {
    slug: 'mobile-app-development',
    name: 'Mobile App Development',
    title: 'Mobile app development for Australian businesses',
    description: 'Build a mobile app that solves a real customer or operational problem, without unnecessary complexity.',
    audience: 'Businesses that need an app for customers, staff or field teams.',
    bullets: ['iOS and Android', 'Flutter or native where appropriate', 'Offline and field workflows', 'Push notifications', 'Store submission'],
    faqs: [
      { question: 'Do you build for iOS and Android?', answer: 'Yes. The right approach depends on the app, the budget and the features.' },
      { question: 'Do you handle app store submission?', answer: 'Yes, we can prepare and submit the app, and help with the review process.' },
      { question: 'Can the app work offline?', answer: 'Yes, if the use case requires it. Offline support is scoped during discovery.' },
    ],
  },
  {
    slug: 'ai-integration',
    name: 'AI Integration',
    title: 'AI integration for Australian businesses',
    description: 'Add AI to an existing product or workflow where it saves time, improves service or unlocks something new.',
    audience: 'Businesses with an existing product or process that could benefit from AI.',
    bullets: ['AI use-case discovery', 'Chat, search and document workflows', 'Model and provider selection', 'Guardrails and evaluation', 'Connection to your systems'],
    faqs: [
      { question: 'Which AI models do you use?', answer: 'We choose based on the use case, cost, privacy and accuracy. We are not tied to one provider.' },
      { question: 'Can you connect AI to our data?', answer: 'Yes, where the data is accessible and the privacy and security requirements are clear.' },
      { question: 'How do you stop the AI making things up?', answer: 'We scope the knowledge, add guardrails, test the outputs and provide a plan for when it can’t answer. No system is perfect, so the workflow needs review.' },
    ],
  },
];

export const demoSteps = [
  'Call the demo number and ask for a quote.',
  'Ask to book a job for this week.',
  'Ask a question the AI should not answer.',
  'Ask for a human.',
  'Notice what it captures and what it sends to the business.',
];
